import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import crypto from "node:crypto";
import { captureServerException } from "@/lib/sentry";
import { createRoutinePrompt, ROUTINE_SYSTEM_PROMPT } from "@/lib/prompts/routine";
import { getRandomRoutineColor } from "@/utilities/routineColors";
import { aiSuggestRoutine } from "@/lib/aiFunctions/aiSuggestRoutine";
import { validateHealthArea } from "@/lib/validation/validateHealthArea";
import { validateUserAI } from "@/lib/validation/validateUser";
import type { Routine } from "instantdb/types";
import { checkUserExistsServer, increaseAISuggestions } from "instantdb/server/userServer";

// Define the input schema using Zod
const suggestRoutineInputSchema = z.object({
  userId: z.string(),
  healthArea: z.any(), // Allow any type for healthArea for now
  language: z.string(),
});

export const suggestRoutine = createServerFn({
  method: "POST",
})
  .validator(suggestRoutineInputSchema)
  .handler(async (ctx) => {
    const { userId, healthArea, language } = ctx.data;
    const goals = healthArea.goals ?? [];
    const routines = healthArea.routines ?? [];

    try {
      // Validate user and health area
      const user = await checkUserExistsServer(userId);
      validateUserAI(user);
      validateHealthArea(healthArea);

      const suggestedRoutine = (await Promise.race([
        aiSuggestRoutine([
          {
            role: "system",
            content: ROUTINE_SYSTEM_PROMPT,
          },
          {
            role: "user",
            content: createRoutinePrompt(healthArea, goals, routines, language),
          },
        ]),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Gemini request timeout")), 30000),
        ),
      ])) as Routine;

      await increaseAISuggestions({ userId });

      const createdRoutine = {
        ...suggestedRoutine,
        steps: (suggestedRoutine.steps ?? []).map((step) => ({
          ...step,
          id: crypto.randomUUID(),
        })),
        healthArea: { id: healthArea.id },
        color: getRandomRoutineColor(),
        userId,
        aiGenerated: true,
        aiAccepted: false,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      return {
        suggestedRoutine: createdRoutine,
      };
    } catch (error: unknown) {
      // Use the safe server-side capture function
      captureServerException(error, {
        tags: {
          endpoint: "suggest-routine-server-fn",
          userId: userId, // Add userId to tags now that it's accessible
        },
      });

      // Re-throw the error to be handled by the client
      throw new Error(
        !import.meta.env.PROD
          ? error instanceof Error
            ? error.message
            : String(error)
          : "Failed to suggest routine",
      );
    }
  });
